import React, { useState, useEffect } from 'react';
import { Box, AppBar, Toolbar, IconButton, Badge, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { styled } from '@mui/material/styles';
import { useNotifications } from '../contexts/NotificationsContext';
import Sidebar from './Sidebar';
import ChatWidget from './chat/ChatWidget';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

const Logo = styled('img')({
  height: '40px',
  marginRight: '20px'
});

const Layout = ({ children }: LayoutProps) => {
  const { userProfile } = useAuth();
  const { notifications, unreadCount } = useNotifications();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Initialize sidebarOpen based on device type and localStorage
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem('sidebarOpen');
    // On mobile, default to closed regardless of saved state
    if (isMobile) return false;
    // On desktop, use saved state or default to open
    return savedState !== null ? savedState === 'true' : true;
  });

  // Save sidebar state to localStorage whenever it changes (desktop only)
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem('sidebarOpen', String(sidebarOpen));
    }
  }, [sidebarOpen, isMobile]);

  // Auto-close sidebar on navigation for mobile
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <Tooltip title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}>
            <IconButton
              color="inherit"
              aria-label={sidebarOpen ? "close drawer" : "open drawer"}
              onClick={toggleSidebar}
              edge="start"
            >
              {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
          
          <Logo src="/samofund-logo.png" alt="Samofund" />
          
          <Box sx={{ flexGrow: 1 }} />
          
          <IconButton color="inherit">
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Sidebar 
        open={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        isMobile={isMobile} // Pass the actual isMobile state instead of hardcoded false
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarOpen ? 280 : 0}px)` },
          marginLeft: { sm: sidebarOpen ? '280px' : 0 },
          transition: theme =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          mt: '64px'
        }}
      >
        {children}
      </Box>

      {/* Floating team chat - available on every authenticated screen. */}
      <ChatWidget />
    </Box>
  );
};

export default Layout; 